/**
 * Keyboard Shortcuts Hook
 * @module hooks/starnexus/useKeyboardShortcuts
 *
 */

import { useEffect, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { announceToScreenReader } from '../../utils/starnexus/accessibility';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  description: string;
  action: () => void;
  category: 'navigation' | 'actions' | 'general';
}

export interface UseKeyboardShortcutsOptions {
  /** Enable/disable all shortcuts */
  enabled?: boolean;
  /** Callback when shortcuts help modal should open */
  onShowHelp?: () => void;
}

export interface UseKeyboardShortcutsReturn {
  /** List of all registered shortcuts */
  shortcuts: KeyboardShortcut[];
  /** Whether shortcuts are currently enabled */
  isEnabled: boolean;
  /** Toggle shortcuts on/off */
  toggleEnabled: () => void;
  /** Whether help modal is visible */
  helpModalVisible: boolean;
  /** Show help modal */
  showHelpModal: () => void;
  /** Hide help modal */
  hideHelpModal: () => void;
}

/**
 * Hook for managing global keyboard shortcuts
 */
export const useKeyboardShortcuts = (options: UseKeyboardShortcutsOptions = {}): UseKeyboardShortcutsReturn => {
  const { enabled = true } = options;
  const navigate = useNavigate();
  const [isEnabled, setIsEnabled] = useState(enabled);
  const [helpModalVisible, setHelpModalVisible] = useState(false);

  const showHelpModal = useCallback(() => {
    setHelpModalVisible(true);
    announceToScreenReader('Keyboard shortcuts help opened');
  }, []);

  const hideHelpModal = useCallback(() => {
    setHelpModalVisible(false);
    announceToScreenReader('Keyboard shortcuts help closed');
  }, []);

  const toggleEnabled = useCallback(() => {
    setIsEnabled((prev) => {
      const newState = !prev;
      announceToScreenReader(newState ? 'Keyboard shortcuts enabled' : 'Keyboard shortcuts disabled');
      return newState;
    });
  }, []);

  // Memoize the shortcut list so the document-level keydown listener below isn't re-bound
  // on every render. Without this, every parent state change tore down and rebuilt the
  // global listener, which both wasted work and very briefly created a window where a
  // keypress could be missed.
  const shortcuts: KeyboardShortcut[] = useMemo(() => [
    {
      key: 'k',
      ctrlKey: true,
      description: 'Focus search',
      category: 'navigation',
      action: () => {
        const searchInput = document.querySelector('[data-testid="search-input"], input[type="search"], input[placeholder*="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          announceToScreenReader('Search focused');
        } else {
          navigate('/starnexus/datasets');
          announceToScreenReader('Navigated to datasets');
        }
      }
    },
    {
      key: 'n',
      ctrlKey: true,
      description: 'Create new dataset',
      category: 'actions',
      action: () => {
        navigate('/starnexus/datasets/create');
        announceToScreenReader('Navigated to create new dataset');
      }
    },
    {
      key: 'h',
      ctrlKey: true,
      description: 'Go to home',
      category: 'navigation',
      action: () => {
        navigate('/starnexus');
        announceToScreenReader('Navigated to home');
      }
    },
    {
      key: 'd',
      ctrlKey: true,
      shiftKey: true,
      description: 'Go to dashboard',
      category: 'navigation',
      action: () => {
        navigate('/starnexus/dashboard');
        announceToScreenReader('Navigated to dashboard');
      }
    },
    {
      key: '/',
      description: 'Show keyboard shortcuts help',
      category: 'general',
      action: showHelpModal,
    },
    {
      key: 'Escape',
      description: 'Close modal/dialog',
      category: 'general',
      action: () => {
        if (helpModalVisible) hideHelpModal();
      },
    },
  ], [navigate, showHelpModal, hideHelpModal, helpModalVisible]);

  useEffect(() => {
    if (!isEnabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      const target = event.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable;

      // Allow Escape to work even in input fields
      if (isInputField && event.key !== 'Escape') {
        return;
      }

      // Find matching shortcut
      const matchingShortcut = shortcuts.find((shortcut) => {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatch = shortcut.ctrlKey ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
        const shiftMatch = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.altKey ? event.altKey : !event.altKey;

        return keyMatch && ctrlMatch && shiftMatch && altMatch;
      });

      if (matchingShortcut) {
        event.preventDefault();
        event.stopPropagation();
        matchingShortcut.action();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEnabled, shortcuts, helpModalVisible, hideHelpModal]);

  return {
    shortcuts,
    isEnabled,
    toggleEnabled,
    helpModalVisible,
    showHelpModal,
    hideHelpModal
  };
};

/**
 * Format shortcut key combination for display
 */
export const formatShortcutKey = (shortcut: KeyboardShortcut): string => {
  const parts: string[] = [];

  // Use ⌘ for Mac, Ctrl for others
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  if (shortcut.ctrlKey || shortcut.metaKey) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shiftKey) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (shortcut.altKey) {
    parts.push(isMac ? '⌥' : 'Alt');
  }

  // Format the key
  let key = shortcut.key;
  if (key === 'Escape') key = 'Esc';
  if (key === '/') key = '/';
  if (key.length === 1) key = key.toUpperCase();

  parts.push(key);

  return parts.join(isMac ? '' : '+');
};

export default useKeyboardShortcuts;
